import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  Sheet,
  SheetCellStyle,
  SheetColumn,
  SheetDetail,
  SheetFilter,
  SheetRow,
  SheetRowData,
  SheetViewConfig,
} from '@yana/shared';
import { todayIso } from '@yana/shared';
import { ApiError } from '@/api/client';
import { sheetsApi } from '@/api/sheets';
import { markError, markSaved, markSaving } from '@/lib/cellDecorations';
import { useToastStore } from '@/stores/toast';
import { allowedSheetObservationView, type SheetObservationView } from '@/lib/sheetObservation';
import { sheetUndoContext, useUndoStack, type UndoRecorder } from '@/composables/useUndoStack';

/**
 * Состояние открытого листа.
 *
 * Отдельно от стора основной таблицы: там колонки известны на сборке,
 * фильтры типизированы по кандидату, есть корзина, группы выезда и
 * realtime по кандидатам. Здесь всё описание приходит с сервера, и общего
 * между ними — только слово «таблица».
 */

/** Раскладка колонок под конкретного человека — в браузере, как у грида. */
const layoutKey = (code: string) => `yana.sheet.${code}.layout`;

interface Layout {
  columns: string[];
  widths: Record<string, number>;
  pinned?: Record<string, 'left' | null>;
}

function readLayout(code: string): Layout | null {
  try {
    const raw = localStorage.getItem(layoutKey(code));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Layout;
    return Array.isArray(parsed.columns) ? parsed : null;
  } catch {
    return null;
  }
}

/** Фильтры ссылок и сотрудников сравнивают подписи, остальных колонок — значения. */
function columnFilterValue(row: SheetRow, column: SheetColumn): unknown {
  if (column.type === 'computed') return row.computed[column.key] ?? null;
  if (column.type === 'ref' || column.type === 'user') {
    return row.labels[column.key] || (row.data[column.key] ?? null);
  }
  return row.data[column.key] ?? null;
}

export const useSheetsStore = defineStore('sheets', () => {
  const toasts = useToastStore();

  /** Список листов: он же меню */
  const sheets = ref<Sheet[]>([]);
  const listLoaded = ref(false);

  const detail = ref<SheetDetail | null>(null);
  const rows = ref<SheetRow[]>([]);
  const total = ref(0);
  /** Сегодня по серверу: заливка по датам считается им, а не часами браузера */
  const today = ref(todayIso());
  const loading = ref(false);
  const error = ref<string | null>(null);

  const search = ref('');
  /**
   * Фильтр по значениям колонки — воронка в заголовке.
   *
   * Считается на клиенте, а не на сервере, хотя сервер это умеет.
   * Причина в самом списке значений: собрать его можно только из строк
   * БЕЗ этого фильтра, иначе список схлопывается до уже выбранного —
   * снял галочку и больше её не вернёшь. Строки листа и так приходят
   * целиком (самый большой — 1440), так что считать не на чем.
   */
  const columnFilters = ref<Record<string, string[]>>({});
  const activeViewId = ref<string | null>(null);
  const sort = ref<{ field: string; dir: 'asc' | 'desc' }[]>([]);
  const visibleKeys = ref<string[] | null>(null);
  const widths = ref<Record<string, number>>({});
  const personalPins = ref<Record<string, 'left' | null>>({});
  // Effective filters may differ from a saved view after a local adjustment,
  // or belong to a colleague's private view while observing.
  const observationFilters = ref<SheetFilter[] | null>(null);
  const temporaryObservationLayout = ref(false);
  const observationActive = ref(false);
  let openSequence = 0;
  let reloadSequence = 0;
  const sessionSequence = ref(0);

  const code = computed(() => detail.value?.sheet.code ?? '');
  const columns = computed(() =>
    (detail.value?.columns ?? []).map((column) =>
      column.key in personalPins.value
        ? { ...column, pinned: personalPins.value[column.key] ?? null }
        : column,
    ),
  );
  const columnByKey = computed(() => new Map(columns.value.map((column) => [column.key, column])));
  const canEdit = computed(() => !observationActive.value && (detail.value?.canEdit ?? false));
  const canManage = computed(() => !observationActive.value && (detail.value?.canManage ?? false));

  const activeView = computed(
    () => detail.value?.views.find((v) => v.id === activeViewId.value) ?? null,
  );
  const activeFilterCount = computed(
    () => (observationFilters.value ?? activeView.value?.config.filters ?? []).length,
  );

  /** Колонки, которые видно: из вида, из личной раскладки или по умолчанию. */
  const visibleColumns = computed<SheetColumn[]>(() => {
    const all = columns.value;
    const keys = visibleKeys.value;
    const selectedKeys = keys ?? all.filter((c) => c.visibleByDefault).map((c) => c.key);

    // Порядок — как в раскладке, но закреплённые всегда слева: иначе
    // закреплённая колонка уезжает в середину и «прилипает» посреди таблицы
    const chosen = selectedKeys
      .map((key) => columnByKey.value.get(key))
      .filter((c): c is SheetColumn => !!c);
    return [
      ...chosen.filter((c) => c.pinned === 'left'),
      ...chosen.filter((c) => c.pinned !== 'left'),
    ];
  });

  // Колонки и выбранные значения разрешаются один раз на смену фильтра,
  // а не линейным поиском для каждой из двенадцати тысяч строк.
  const preparedColumnFilters = computed(() =>
    Object.entries(columnFilters.value).flatMap(([key, values]) => {
      const column = columnByKey.value.get(key);
      return column && values.length ? [{ key, column, values: new Set(values) }] : [];
    }),
  );

  /** Строки после фильтров по колонкам — то, что видно в таблице. */
  const visibleRows = computed<SheetRow[]>(() => {
    const active = preparedColumnFilters.value;
    if (!active.length) return rows.value;

    return rows.value.filter((row) =>
      active.every(({ column, values }) => matchesColumnFilter(row, column, values)),
    );
  });

  function matchesColumnFilter(row: SheetRow, column: SheetColumn, values: ReadonlySet<string>): boolean {
    const value = columnFilterValue(row, column);
    // Пустая ячейка — такое же значение, как остальные: в книге
    // «не заполнено» ищут не реже, чем конкретный объект
    if (Array.isArray(value)) return value.some((v) => values.has(String(v)));
    return values.has(value === null || value === undefined ? '' : String(value));
  }

  /** Все значения колонки — из строк без учёта её собственного фильтра. */
  function distinctValues(key: string): string[] {
    const column = columnByKey.value.get(key);
    if (!column) return [];

    const others = preparedColumnFilters.value.filter((filter) => filter.key !== key);

    const seen = new Set<string>();
    for (const row of rows.value) {
      const passes = others.every(({ column, values }) => matchesColumnFilter(row, column, values));
      if (!passes) continue;

      const value = columnFilterValue(row, column);

      if (Array.isArray(value)) value.forEach((v) => seen.add(String(v)));
      else seen.add(value === null || value === undefined ? '' : String(value));
    }

    return [...seen].sort((a, b) => {
      // Пустое — в конец: его ищут реже, чем значения
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b, 'ru');
    });
  }

  async function setColumnFilter(key: string, values: string[] | null): Promise<void> {
    const next = { ...columnFilters.value };
    if (!values || !values.length) delete next[key];
    else next[key] = values;
    columnFilters.value = next;
    // A preset's `in` filter was also applied on the server. Keeping it there
    // would make clearing or widening the visible column filter ineffective.
    const current = observationFilters.value ?? activeView.value?.config.filters ?? [];
    const remaining = current.filter((filter) => filter.field !== key || filter.op !== 'in');
    if (remaining.length !== current.length) {
      observationFilters.value = remaining;
      await reload();
    }
  }

  function clearColumnFilters(): void {
    columnFilters.value = {};
  }

  function widthOf(column: SheetColumn): number {
    return widths.value[column.key] ?? column.width;
  }

  async function loadList(force = false): Promise<void> {
    if (listLoaded.value && !force) return;
    const session = sessionSequence.value;
    try {
      const loaded = await sheetsApi.list();
      if (session !== sessionSequence.value) return;
      sheets.value = loaded;
      listLoaded.value = true;
    } catch {
      if (session !== sessionSequence.value) return;
      // Меню без листов — не повод показывать ошибку на весь экран:
      // остальные разделы работают
      sheets.value = [];
    }
  }

  function applyView(config: SheetViewConfig | null): void {
    if (!config) return;
    if (config.columns.length) visibleKeys.value = [...config.columns];
    if (config.sort?.length) sort.value = config.sort.map((s) => ({ field: s.field, dir: s.dir }));
    // Фильтры вида по значениям колонки возвращаются в воронки заголовков
    for (const filter of config.filters ?? []) {
      if (filter.op === 'in' && Array.isArray(filter.value)) {
        columnFilters.value = {
          ...columnFilters.value,
          [filter.field]: filter.value.map(String),
        };
      }
    }
    if (config.search !== undefined) search.value = config.search ?? '';
    widths.value = { ...config.widths };
  }

  async function open(sheetCode: string): Promise<void> {
    const request = ++openSequence;
    reloadSequence += 1;
    loading.value = true;
    error.value = null;
    try {
      const loaded = await sheetsApi.detail(sheetCode);
      if (request !== openSequence) return;
      detail.value = loaded;
      observationFilters.value = null;
      temporaryObservationLayout.value = false;
      personalPins.value = {};

      const layout = readLayout(sheetCode);
      const defaultView =
        detail.value.views.find((v) => v.isDefault) ?? detail.value.views[0] ?? null;

      activeViewId.value = defaultView?.id ?? null;
      columnFilters.value = {};
      visibleKeys.value = null;
      sort.value = [];
      search.value = '';
      widths.value = {};

      applyView(defaultView?.config ?? null);
      // Личная раскладка сильнее вида: человек перетаскивал колонки под себя
      if (layout) {
        visibleKeys.value = layout.columns.filter((key) =>
          detail.value!.columns.some((c) => c.key === key),
        );
        widths.value = { ...widths.value, ...layout.widths };
        personalPins.value = layout.pinned ?? {};
      }

      await reload();
    } catch (e) {
      if (request !== openSequence) return;
      error.value = e instanceof ApiError ? e.message : 'Лист не открылся';
      detail.value = null;
      rows.value = [];
    } finally {
      if (request === openSequence) loading.value = false;
    }
  }

  async function reload(): Promise<void> {
    if (!detail.value) return;
    const request = ++reloadSequence;
    const sheetCode = detail.value.sheet.code;
    loading.value = true;
    try {
      const result = await sheetsApi.rows(sheetCode, {
        search: search.value || undefined,
        filters: observationFilters.value ?? activeView.value?.config.filters ?? [],
        sort: sort.value,
        // Строк на «Базе» почти двенадцать тысяч, и берутся они одним
        // запросом: таблица виртуализирована по строкам и колонкам, а ответ
        // уходит под gzip обратного прокси — 4,4 МБ становятся 370 КБ
        limit: 20000,
      });
      if (request !== reloadSequence || code.value !== sheetCode) return;
      rows.value = result.rows;
      total.value = result.total;
      today.value = result.today;
      /*
        Удачное перечитывание снимает прежнюю ошибку.

        Без этой строки одна неудачная загрузка — оборвалась связь на щелчке
        по сортировке — навсегда подменяла таблицу карточкой «Лист не открылся»:
        строки лежали в памяти, поиск и правка работали, а на экране была
        заглушка, и вернуть таблицу можно было только уходом на другой лист.
      */
      error.value = null;
    } catch (e) {
      if (request !== reloadSequence || code.value !== sheetCode) return;
      error.value = e instanceof ApiError ? e.message : 'Данные листа не загрузились';
    } finally {
      if (request === reloadSequence) loading.value = false;
    }
  }

  async function selectView(viewId: string | null): Promise<void> {
    observationFilters.value = null;
    activeViewId.value = viewId;
    const view = detail.value?.views.find((v) => v.id === viewId) ?? null;
    /*
      Переключение вида сбрасывает раскладку целиком, а не подмешивается
      к прежней. На основной таблице это уже было ошибкой: срез «врал» —
      человек переключался, а фильтр от предыдущего оставался.
    */
    visibleKeys.value = null;
    widths.value = {};
    sort.value = [];
    columnFilters.value = {};
    search.value = '';
    applyView(view?.config ?? null);
    await reload();
  }

  /** Снять условия выборки, сохранив выбранные колонки, ширины и сортировку. */
  async function resetFilters(): Promise<void> {
    search.value = '';
    columnFilters.value = {};
    activeViewId.value = null;
    observationFilters.value = null;
    await reload();
  }

  function captureObservationView(): SheetObservationView {
    return {
      activeViewId: activeViewId.value,
      search: search.value,
      sort: sort.value.map((item) => ({ ...item })),
      filters: JSON.parse(
        JSON.stringify(observationFilters.value ?? activeView.value?.config.filters ?? []),
      ),
      columnFilters: Object.fromEntries(
        Object.entries(columnFilters.value).map(([key, values]) => [key, [...values]]),
      ),
      columns: visibleColumns.value.map((column) => column.key),
      widths: Object.fromEntries(columns.value.map((column) => [column.key, widthOf(column)])),
      pins: Object.fromEntries(columns.value.map((column) => [column.key, column.pinned])),
    };
  }

  /** A changed login must discard previous data instead of restoring its saved observation view. */
  function discardObservation(): void {
    openSequence += 1;
    reloadSequence += 1;
    sessionSequence.value += 1;
    observationFilters.value = null;
    temporaryObservationLayout.value = false;
    observationActive.value = false;
    sheets.value = [];
    listLoaded.value = false;
    detail.value = null;
    rows.value = [];
    total.value = 0;
    loading.value = false;
    error.value = null;
    search.value = '';
    sort.value = [];
    activeViewId.value = null;
    columnFilters.value = {};
    visibleKeys.value = null;
    widths.value = {};
    personalPins.value = {};
  }

  /** Apply only local display state; never call layout persistence or write endpoints. */
  async function applyObservationView(input: unknown, temporary = true): Promise<boolean> {
    const view = allowedSheetObservationView(
      input,
      columns.value.map((column) => column.key),
    );
    if (!view) return false;
    const previousQuery = JSON.stringify({
      search: search.value,
      sort: sort.value,
      filters: observationFilters.value ?? activeView.value?.config.filters ?? [],
    });
    activeViewId.value = view.activeViewId;
    search.value = view.search;
    sort.value = view.sort;
    // Restore local adjustments too: the saved preset may still contain a
    // filter the user removed before beginning observation.
    observationFilters.value = view.filters;
    temporaryObservationLayout.value = temporary;
    columnFilters.value = view.columnFilters;
    visibleKeys.value = view.columns;
    widths.value = view.widths;
    personalPins.value = view.pins;
    if (
      previousQuery !==
      JSON.stringify({
        search: view.search,
        sort: view.sort,
        filters: view.filters,
      })
    ) {
      await reload();
    }
    return true;
  }

  /** Правка одной ячейки. */
  async function setValue(
    rowId: string,
    key: string,
    value: unknown,
    strict = false,
  ): Promise<boolean> {
    if (observationActive.value) return false;
    if (!detail.value) return false;
    const sheetCode = code.value;
    const row = rows.value.find((r) => r.id === rowId);
    if (!row) {
      if (!strict) return false;
      const result = await sheetsApi.patchCells(sheetCode, [{ rowId, data: { [key]: value } }]);
      if (code.value === sheetCode) applyFresh(result.rows);
      return true;
    }

    const before = row.data[key] ?? null;
    if (JSON.stringify(before) === JSON.stringify(value ?? null)) return false;

    // Значение показывается сразу, до ответа сервера: иначе на каждой правке
    // ячейка на мгновение возвращается к прежнему тексту
    row.data = { ...row.data, [key]: value };
    /*
      Состояние правки ставится классом прямо на элемент, мимо реактивности.

      В основной таблице это уже выстрадано: пока «сохраняется / сохранено»
      лежало в сторе, правка ОДНОЙ ячейки заставляла Vue пересчитать классы
      у всех видимых — а их под тысячу, и трижды подряд на одну правку.
      Отсюда и мигало всё разом. Здесь та же беда была бы ровно такой же.
    */
    markSaving(rowId, key);

    try {
      /*
        Вычисляемые колонки считает сервер: после правки даты выезда должна
        измениться и дата окончания. Раньше ради этого лист перечитывался
        целиком — полторы тысячи строк после каждой правки одной ячейки,
        и на слабой сети это выглядело как зависание. Теперь пересчитанная
        строка приезжает в ответе на саму правку.
      */
      const { rows: fresh } = await sheetsApi.patchCells(sheetCode, [
        { rowId, data: { [key]: value } },
      ]);
      if (code.value === sheetCode) applyFresh(fresh);
      markSaved(rowId, key);
      return true;
    } catch (e) {
      row.data = { ...row.data, [key]: before };
      markError(rowId, key);
      toasts.error(e instanceof ApiError ? e.message : 'Правка не сохранилась');
      if (strict) throw e;
      return false;
    }
  }

  /**
   * Правка нескольких ячеек одной операцией.
   *
   * Так приходит вставка блока из Excel, протягивание вниз и очистка
   * диапазона. Возвращает прежние значения — по ним работает отмена:
   * вставка блока отменяется целиком, а не по ячейке.
   */
  async function applyCells(
    changes: Array<{ rowId: string; key: string; value: unknown }>,
    strict = false,
  ): Promise<Array<{ rowId: string; key: string; value: unknown }>> {
    if (observationActive.value) return [];
    if (!detail.value || !changes.length) return [];

    const byRow = new Map<string, Record<string, unknown>>();
    const rowById = new Map(rows.value.map((row) => [row.id, row]));
    const before: Array<{ rowId: string; key: string; value: unknown }> = [];

    for (const change of changes) {
      const row = rowById.get(change.rowId);
      if (!row && !strict) continue;
      before.push({
        rowId: change.rowId,
        key: change.key,
        value: row?.data[change.key] ?? null,
      });
      const patch = byRow.get(change.rowId) ?? {};
      patch[change.key] = change.value;
      byRow.set(change.rowId, patch);
    }

    // Значения показываем сразу: ответа ждут сотни ячеек, и мигание
    // «вставилось — пропало — появилось» читается как сбой
    for (const [rowId, patch] of byRow) {
      const row = rowById.get(rowId);
      if (!row) continue;
      row.data = { ...row.data, ...patch };
      for (const key of Object.keys(patch)) markSaving(rowId, key);
    }

    const sheetCode = detail.value.sheet.code;
    try {
      /*
        Один запрос на весь прямоугольник, а не запрос на строку в цикле.

        Вставка блока 20×5 давала двадцать запросов подряд, двадцать записей
        в журнале и — при вычисляемой колонке — двадцать полных перечитываний
        листа. Сервер пишет их одной пачкой под общим `batchId` и возвращает
        затронутые строки пересчитанными.
      */
      const { rows: fresh } = await sheetsApi.patchCells(
        sheetCode,
        [...byRow].map(([rowId, data]) => ({ rowId, data })),
      );
      if (code.value === sheetCode) applyFresh(fresh);
      for (const change of changes) markSaved(change.rowId, change.key);
    } catch (e) {
      for (const change of changes) markError(change.rowId, change.key);
      toasts.error(e instanceof ApiError ? e.message : 'Правка не сохранилась');
      if (code.value === sheetCode) await reload();
      if (strict) throw e;
      return [];
    }

    return before;
  }

  /**
   * Подстановка пересчитанных строк на их места.
   *
   * Строка заменяется целиком, а не полем: вместе со значением приезжают
   * вычисленные колонки, подписи ссылок и оформление, и собирать их по
   * кусочкам значило бы держать здесь второе знание о том, из чего состоит
   * строка.
   */
  function applyFresh(fresh: SheetRow[]): void {
    if (!fresh.length) return;
    const byId = new Map(fresh.map((row) => [row.id, row]));
    rows.value = rows.value.map((row) => byId.get(row.id) ?? row);
  }

  /**
   * Заливка нескольких ячеек одной операцией.
   *
   * Возвращает прежние цвета — по ним работает отмена. Пока заливка шла
   * запросом на ячейку, покраска прямоугольника 20×5 давала двадцать шагов
   * отмены: человек красил кусок, жал Ctrl+Z и получал обратно одну ячейку.
   */
  function formattingStillApplied<T>(
    sheetCode: string,
    patches: ReadonlyMap<string, Readonly<Record<string, T>>>,
    matches: (row: SheetRow, key: string, value: T) => boolean,
  ): boolean {
    if (code.value !== sheetCode) return false;
    // Нужны текущие строки: завершившийся GET заменяет объекты старого Map.
    const currentRows = new Map(rows.value.map((row) => [row.id, row]));
    return [...patches].every(([rowId, patch]) => {
      const row = currentRows.get(rowId);
      return !!row && Object.entries(patch).every(([key, value]) => matches(row, key, value));
    });
  }

  async function applyFills(
    cells: Array<{ rowId: string; key: string; fill: string | null }>,
    strict = false,
  ): Promise<Array<{ rowId: string; key: string; fill: string | null }>> {
    if (observationActive.value) return [];
    if (!detail.value || !cells.length) return [];
    const sheetCode = code.value;

    const byRow = new Map<string, Record<string, string | null>>();
    const rowById = new Map(rows.value.map((row) => [row.id, row]));
    const before: Array<{ rowId: string; key: string; fill: string | null }> = [];

    for (const cell of cells) {
      const row = rowById.get(cell.rowId);
      if (!row && !strict) continue;
      before.push({
        rowId: cell.rowId,
        key: cell.key,
        fill: row?.fills[cell.key] ?? null,
      });
      const patch = byRow.get(cell.rowId) ?? {};
      patch[cell.key] = cell.fill;
      byRow.set(cell.rowId, patch);
    }
    if (!byRow.size) return [];

    // Цвет показывается сразу: ответа ждут сотни ячеек
    for (const [rowId, patch] of byRow) {
      const row = rowById.get(rowId);
      if (!row) continue;
      const next = { ...row.fills };
      for (const [key, value] of Object.entries(patch)) {
        if (value) next[key] = value;
        else delete next[key];
      }
      row.fills = next;
    }

    try {
      await sheetsApi.patchCells(
        sheetCode,
        [...byRow].map(([rowId, fills]) => ({ rowId, fills })),
        { isApplied: () => formattingStillApplied(sheetCode, byRow,
          (row, key, value) => (row.fills[key] ?? null) === value) },
      );
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Заливка не сохранилась');
      if (code.value === sheetCode) await reload();
      if (strict) throw e;
      return [];
    }

    return before;
  }

  /** Оформление нескольких ячеек одной операцией: те же правила, что у заливки. */
  async function applyStyles(
    cells: Array<{ rowId: string; key: string; style: SheetCellStyle | null }>,
    strict = false,
  ): Promise<Array<{ rowId: string; key: string; style: SheetCellStyle | null }>> {
    if (observationActive.value) return [];
    if (!detail.value || !cells.length) return [];
    const sheetCode = code.value;

    const byRow = new Map<string, Record<string, SheetCellStyle | null>>();
    const rowById = new Map(rows.value.map((row) => [row.id, row]));
    const before: Array<{
      rowId: string;
      key: string;
      style: SheetCellStyle | null;
    }> = [];

    for (const cell of cells) {
      const row = rowById.get(cell.rowId);
      if (!row && !strict) continue;
      before.push({
        rowId: cell.rowId,
        key: cell.key,
        style: row?.styles?.[cell.key] ?? null,
      });
      const patch = byRow.get(cell.rowId) ?? {};
      // Пустой объект на сервере снимает оформление. Копия также защищает
      // проверку WS-эха от последующего изменения объекта вызывающей стороной.
      const style = cell.style
        ? Object.fromEntries(Object.entries(cell.style).filter(([, value]) => value !== undefined)) as SheetCellStyle
        : null;
      patch[cell.key] = style && Object.keys(style).length ? style : null;
      byRow.set(cell.rowId, patch);
    }
    if (!byRow.size) return [];

    for (const [rowId, patch] of byRow) {
      const row = rowById.get(rowId);
      if (!row) continue;
      const next = { ...(row.styles ?? {}) };
      for (const [key, value] of Object.entries(patch)) {
        if (value) next[key] = value;
        else delete next[key];
      }
      row.styles = next;
    }

    try {
      await sheetsApi.patchCells(
        sheetCode,
        [...byRow].map(([rowId, styles]) => ({ rowId, styles })),
        { isApplied: () => formattingStillApplied(sheetCode, byRow, (row, key, value) => {
          const current = row.styles?.[key];
          if (!value) return !current || !Object.keys(current).length;
          return !!current && Object.keys(current).length === Object.keys(value).length &&
            Object.entries(value).every(([property, expected]) => current[property as keyof SheetCellStyle] === expected);
        }) },
      );
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Оформление не сохранилось');
      if (code.value === sheetCode) await reload();
      if (strict) throw e;
      return [];
    }

    return before;
  }

  /**
   * Новая строка на своё место.
   *
   * Порядок — среднее между соседями: строка встаёт между ними, а весь
   * хвост листа не переписывается. Секцию берём у соседа сверху, иначе
   * строка, вставленная внутрь «МОСКВА и МО», уехала бы из секции.
   */
  async function insertRow(anchorId: string, where: 'above' | 'below'): Promise<void> {
    if (observationActive.value) return;
    if (!detail.value) return;
    const anchor = rows.value.find((row) => row.id === anchorId);
    const list = rows.value
      .filter((row) => row.sectionId === anchor?.sectionId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const index = list.findIndex((row) => row.id === anchorId);
    if (!anchor) return void addRow();

    const neighbour = where === 'above' ? list[index - 1] : list[index + 1];
    const low =
      where === 'above' ? (neighbour?.sortOrder ?? anchor.sortOrder - 20) : anchor.sortOrder;
    const high =
      where === 'above' ? anchor.sortOrder : (neighbour?.sortOrder ?? anchor.sortOrder + 20);

    try {
      const sheetCode = detail.value.sheet.code;
      const recordUndo = useUndoStack(sheetUndoContext(sheetCode)).capture();
      const created = await sheetsApi.createRow(sheetCode, {}, anchor.sectionId, (low + high) / 2);
      if (code.value === sheetCode) await reload();
      recordAddedRow(sheetCode, created.id, recordUndo);
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строка не добавилась');
    }
  }

  /**
   * Дописать строки в конец листа и вернуть их идентификаторы.
   *
   * Нужно вставке из буфера: блок из Excel обычно длиннее того, что есть
   * на листе, и в книге строки при вставке добавляются сами. Молча терять
   * хвост нельзя — это ровно те данные, ради которых блок и копировали.
   */
  async function appendRows(count: number): Promise<string[]> {
    if (observationActive.value) return [];
    if (!detail.value || count <= 0) return [];
    const sheetCode = detail.value.sheet.code;
    const last = rows.value.reduce<SheetRow | undefined>(
      (found, row) => (!found || row.sortOrder > found.sortOrder ? row : found),
      undefined,
    );
    const ids: string[] = [];

    try {
      for (let i = 0; i < count; i += 1) {
        const created = await sheetsApi.createRow(sheetCode, {}, last?.sectionId ?? null);
        ids.push(created.id);
      }
      if (code.value === sheetCode) await reload();
      return ids;
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строки не добавились');
      if (code.value === sheetCode) await reload();
      return ids;
    }
  }

  async function deleteRows(ids: string[], strict = false): Promise<string[]> {
    if (observationActive.value) return [];
    if (!detail.value || !ids.length) return [];
    const sheetCode = detail.value.sheet.code;
    const deleted: string[] = [];
    try {
      for (const id of ids) {
        await sheetsApi.deleteRow(sheetCode, id);
        deleted.push(id);
      }
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строки не удалились');
      if (strict) throw e;
    } finally {
      if (code.value === sheetCode) {
        rows.value = rows.value.filter((row) => !deleted.includes(row.id));
        total.value = Math.max(0, total.value - deleted.length);
      }
    }
    return deleted;
  }

  async function restoreRows(ids: string[], strict = false): Promise<void> {
    if (observationActive.value) return;
    if (!detail.value || !ids.length) return;
    const sheetCode = detail.value.sheet.code;
    try {
      for (const id of ids) await sheetsApi.restoreRow(sheetCode, id);
      if (code.value === sheetCode) await reload();
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строки не вернулись');
      if (strict) throw e;
    }
  }

  async function setFill(rowId: string, key: string, color: string | null): Promise<void> {
    if (observationActive.value) return;
    if (!detail.value) return;
    const row = rows.value.find((r) => r.id === rowId);
    if (!row) return;
    const before = { ...row.fills };
    const next = { ...row.fills };
    if (color) next[key] = color;
    else delete next[key];
    row.fills = next;

    try {
      await sheetsApi.patchRow(detail.value.sheet.code, rowId, {
        fills: { [key]: color },
      });
    } catch (e) {
      row.fills = before;
      toasts.error(e instanceof ApiError ? e.message : 'Заливка не сохранилась');
    }
  }

  function recordAddedRow(sheetCode: string, id: string, recordUndo: UndoRecorder): void {
    recordUndo({
      label: 'Добавление строки',
      undo: async () => {
        await sheetsApi.deleteRow(sheetCode, id);
        if (code.value === sheetCode) await reload();
      },
      redo: async () => {
        await sheetsApi.restoreRow(sheetCode, id);
        if (code.value === sheetCode) await reload();
      },
    });
  }

  async function addRow(sectionId: string | null = null): Promise<string | undefined> {
    if (observationActive.value) return;
    if (!detail.value) return;
    try {
      const sheetCode = detail.value.sheet.code;
      const recordUndo = useUndoStack(sheetUndoContext(sheetCode)).capture();
      const created = await sheetsApi.createRow(sheetCode, {}, sectionId);
      if (code.value === sheetCode) await reload();
      recordAddedRow(sheetCode, created.id, recordUndo);
      return created.id;
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строка не добавилась');
    }
  }

  async function removeRow(rowId: string): Promise<void> {
    if (observationActive.value) return;
    if (!detail.value) return;
    try {
      await sheetsApi.deleteRow(detail.value.sheet.code, rowId);
      rows.value = rows.value.filter((r) => r.id !== rowId);
      total.value = Math.max(0, total.value - 1);
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строка не удалилась');
    }
  }

  function setVisible(keys: string[]): void {
    visibleKeys.value = keys;
    persistLayout();
  }

  function setPinned(key: string, value: 'left' | null, sheetCode = code.value): void {
    if (sheetCode !== code.value) {
      const layout = readLayout(sheetCode);
      if (layout) {
        layout.pinned = { ...layout.pinned, [key]: value };
        try {
          localStorage.setItem(layoutKey(sheetCode), JSON.stringify(layout));
        } catch {
          /* Личная раскладка недоступна. Общее закрепление сохранено сервером. */
        }
      }
      return;
    }
    personalPins.value = { ...personalPins.value, [key]: value };
    persistLayout();
  }

  function setWidth(key: string, width: number): void {
    widths.value = { ...widths.value, [key]: width };
    persistLayout();
  }

  /** Сортировка из меню колонки: направление задано явно. */
  function setSort(key: string, dir: 'asc' | 'desc' | null): void {
    sort.value = dir ? [{ field: key, dir }] : [];
    void reload();
  }

  /**
   * Ширина колонки после перетаскивания края.
   *
   * У того, кто распоряжается листом, ширина общая: в книге её тянут
   * для всех, и «у меня узко, а у соседа широко» читается как поломка.
   * Остальным ширина остаётся личной — в браузере.
   */
  async function commitWidth(key: string, width: number): Promise<void> {
    if (observationActive.value) return;
    setWidth(key, width);
    if (!canManage.value || !detail.value) return;
    const column = detail.value.columns.find((c) => c.key === key);
    if (!column) return;
    try {
      await sheetsApi.updateColumn(detail.value.sheet.code, column.id, {
        width,
      });
      column.width = width;
    } catch {
      // Не сохранилось — ширина всё равно применена локально
    }
  }

  function toggleSort(key: string): void {
    const current = sort.value[0];
    if (!current || current.field !== key) sort.value = [{ field: key, dir: 'asc' }];
    else if (current.dir === 'asc') sort.value = [{ field: key, dir: 'desc' }];
    // Третье нажатие снимает сортировку и возвращает порядок листа —
    // в книге он смысловой: объекты идут не по алфавиту
    else sort.value = [];
    void reload();
  }

  function persistLayout(): void {
    if (!code.value || temporaryObservationLayout.value) return;
    try {
      localStorage.setItem(
        layoutKey(code.value),
        JSON.stringify({
          columns: visibleColumns.value.map((c) => c.key),
          widths: widths.value,
          pinned: personalPins.value,
        }),
      );
    } catch {
      // Хранилище закрыто политикой браузера — раскладка живёт до перезагрузки
    }
  }

  async function refreshDetail(): Promise<void> {
    if (!detail.value) return;
    const sheetCode = code.value;
    const request = openSequence;
    const fresh = await sheetsApi.detail(sheetCode);
    if (code.value === sheetCode && request === openSequence) detail.value = fresh;
  }

  /** Компенсации адресованы исходному листу даже между несколькими await. */
  function historyActionsFor(sheetCode: string) {
    const refresh = async () => {
      if (code.value === sheetCode) await reload();
    };
    const patch = async (changes: Parameters<typeof sheetsApi.patchCells>[1]) => {
      await sheetsApi.patchCells(sheetCode, changes);
      await refresh();
    };
    function combine<T>(
      cells: Array<{ rowId: string; key: string } & T>,
      read: (cell: T) => unknown,
    ) {
      const byRow = new Map<string, Record<string, unknown>>();
      for (const cell of cells) {
        const data = byRow.get(cell.rowId) ?? {};
        data[cell.key] = read(cell);
        byRow.set(cell.rowId, data);
      }
      return [...byRow];
    }
    return {
      applyCells: (cells: Array<{ rowId: string; key: string; value: unknown }>) =>
        patch(
          combine(cells, (cell) => cell.value).map(([rowId, data]) => ({
            rowId,
            data,
          })),
        ),
      applyFills: (cells: Array<{ rowId: string; key: string; fill: string | null }>) =>
        patch(
          combine(cells, (cell) => cell.fill).map(([rowId, fills]) => ({
            rowId,
            fills: fills as Record<string, string | null>,
          })),
        ),
      applyStyles: (
        cells: Array<{
          rowId: string;
          key: string;
          style: SheetCellStyle | null;
        }>,
      ) =>
        patch(
          combine(cells, (cell) => cell.style).map(([rowId, styles]) => ({
            rowId,
            styles: styles as Record<string, SheetCellStyle | null>,
          })),
        ),
      deleteRows: async (ids: string[]) => {
        for (const id of ids) await sheetsApi.deleteRow(sheetCode, id);
        await refresh();
      },
      restoreRows: async (ids: string[]) => {
        for (const id of ids) await sheetsApi.restoreRow(sheetCode, id);
        await refresh();
      },
    };
  }

  return {
    sheets,
    detail,
    rows,
    visibleRows,
    columnFilters,
    distinctValues,
    setColumnFilter,
    clearColumnFilters,
    total,
    today,
    loading,
    error,
    search,
    sort,
    activeViewId,
    activeView,
    activeFilterCount,
    code,
    columns,
    visibleColumns,
    canEdit,
    canManage,
    widthOf,
    loadList,
    open,
    reload,
    selectView,
    resetFilters,
    setValue,
    setFill,
    addRow,
    removeRow,
    applyCells,
    applyFills,
    applyStyles,
    appendRows,
    widths,
    insertRow,
    deleteRows,
    restoreRows,
    setVisible,
    setWidth,
    setPinned,
    commitWidth,
    setSort,
    toggleSort,
    refreshDetail,
    historyActionsFor,
    captureObservationView,
    discardObservation,
    observationSession: computed(() => sessionSequence.value),
    applyObservationView,
    temporaryObservationLayout,
    observationActive,
  };
});

export type { SheetRowData };
